import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import { useEventListener } from '@vueuse/core'
import { productTourInjectionKey } from '@/composables/productTour'

interface ProductTourOverlayProps {
  /**
   * Background overlay opacity (0-1)
   */
  overlayOpacity?: number
  /**
   * Border radius for the spotlight
   */
  spotlightRadius?: number
  /**
   * Padding around the highlighted element
   */
  spotlightPadding?: number
  /**
   * Whether elements should be interactive
   */
  allowInteraction?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductTourOverlay',
  props: {
    overlayOpacity: { default: 0.8 },
    spotlightRadius: { default: 8 },
    spotlightPadding: { default: 8 },
    allowInteraction: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props as { overlayOpacity: number, spotlightRadius: number, spotlightPadding: number, allowInteraction: boolean };



// Inject tour state
const tour = inject(productTourInjectionKey)
if (!tour) {
  throw new Error(
    'Unknown product tour context, did you forget to wrap <ProductTour> around this component?',
  )
}

// Spotlight position and size
const spotlightStyle = ref({
  width: '0px',
  height: '0px',
  transform: 'translateX(0px) translateY(0px)',
  borderRadius: `${props.spotlightRadius}px`,
})

// Calculate spotlight position and size based on active element
function updateSpotlight() {
  if (!tour?.isActive.value || !tour?.currentStep.value?.element) {
    return
  }

  const element = tour.currentStep.value.element
  const rect = element.getBoundingClientRect()

  const width = rect.width + props.spotlightPadding * 2
  const height = rect.height + props.spotlightPadding * 2
  const x = rect.left - props.spotlightPadding
  const y = rect.top - props.spotlightPadding

  spotlightStyle.value = {
    width: `${width}px`,
    height: `${height}px`,
    transform: `translateX(${x}px) translateY(${y}px)`,
    borderRadius: `${props.spotlightRadius}px`,
  }
}

// Watch for tour state changes
watch(
  () => [tour?.isActive.value, tour?.currentStep.value],
  async () => {
    if (tour?.isActive.value) {
      await nextTick()
      updateSpotlight()
    }
  },
  { immediate: true },
)

// Update spotlight on window resize
onMounted(() => {
  const handleResize = () => {
    if (tour?.isActive.value) {
      updateSpotlight()
    }
  }

  useEventListener(window, 'resize', handleResize)
})

// Computed style for overlay background
const overlayStyle = computed(() => ({
  opacity: tour?.isActive.value ? 1 : 0,
  pointerEvents: (props.allowInteraction ? 'none' : 'auto') as 'none' | 'auto',
}))

// Box shadow for spotlight effect
const spotlightBoxShadow = computed(() => {
  const shadowColor = `rgba(0, 0, 0, ${props.overlayOpacity})`
  return `${shadowColor} 0px 0px 200vw 200vh`
})

return { __sfc: true,props, tour, spotlightStyle, updateSpotlight, overlayStyle, spotlightBoxShadow }
}

})